import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TestResult } from '../types';
import { exportToExcel, exportToCSV, exportToJSON, getExportSizeEstimate } from '../utils/exportUtils';

interface ExportMenuProps {
  results: TestResult[];
  disabled?: boolean;
}

type ExportFormat = 'xlsx' | 'csv' | 'json';

interface ExportOption {
  format: ExportFormat;
  label: string;
  icon: JSX.Element;
  description: string;
}

export default function ExportMenu({ results, disabled = false }: ExportMenuProps) {
  const { t } = useTranslation(['app', 'common']);
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const exportOptions: ExportOption[] = [
    {
      format: 'xlsx',
      label: 'Excel (.xlsx)',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      description: 'Formato profesional con columnas ajustadas',
    },
    {
      format: 'csv',
      label: 'CSV',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
      description: 'Compatible con Excel y bases de datos',
    },
    {
      format: 'json',
      label: 'JSON',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
          />
        </svg>
      ),
      description: 'Datos completos para procesamiento',
    },
  ];

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleExport = async (format: ExportFormat) => {
    setIsExporting(true);
    setIsOpen(false);

    try {
      let filename: string;

      switch (format) {
        case 'xlsx':
          filename = exportToExcel(results);
          break;
        case 'csv':
          filename = exportToCSV(results);
          break;
        case 'json':
          filename = exportToJSON(results);
          break;
      }

      toast.success(`Exportado con éxito: ${filename}`);

      // Optional: Add a brief delay to show the loading state
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('Error al exportar los datos. Por favor, intenta de nuevo.');
    } finally {
      setIsExporting(false);
    }
  };

  const resultCount = results.length;
  const isDisabled = disabled || resultCount === 0 || isExporting;

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
        className={`
          flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors
          ${
            isDisabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
          }
        `}
        aria-label="Exportar datos"
      >
        {isExporting ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="hidden sm:inline">Exportando...</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            <span className="hidden sm:inline">Exportar</span>
            <svg
              className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && !isDisabled && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
          <div className="p-3 bg-gray-50 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-700">
              Exportar {resultCount} resultado{resultCount !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="py-2">
            {exportOptions.map((option) => {
              const sizeEstimate = getExportSizeEstimate(results, option.format);

              return (
                <button
                  key={option.format}
                  onClick={() => handleExport(option.format)}
                  className="w-full px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3 text-left"
                >
                  <div className="flex-shrink-0 mt-0.5 text-green-600">
                    {option.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{option.label}</p>
                      <span className="text-xs text-gray-500 ml-2">~{sizeEstimate}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="p-3 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-gray-600">
              💡 El archivo se descargará automáticamente
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
